import { useState } from "react";
import type { AdminUserCreate } from "../api/admin";
import { createUser } from "../api/admin";
import { ApiError } from "../api/client";

type Props = {
  onCreated: () => void;
  onCancel: () => void;
};

export function AddUserForm({ onCreated, onCancel }: Props) {
  const [form, setForm] = useState<AdminUserCreate>({
    username: "",
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    designation: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function update<K extends keyof AdminUserCreate>(key: K, value: AdminUserCreate[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload: AdminUserCreate = {
        username: form.username.trim(),
        email: form.email.trim(),
        password: form.password,
        first_name: form.first_name?.trim() || null,
        last_name: form.last_name?.trim() || null,
        designation: form.designation?.trim() || null,
      };
      await createUser(payload);
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Failed to create user.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="my-6 rounded-lg border border-terra/40 bg-terra/5 p-6"
    >
      <div className="mb-4 flex items-baseline justify-between border-b border-rule-soft pb-3">
        <h2 className="font-display text-xl tracking-tight [font-variation-settings:'opsz'_22,'SOFT'_30,'WONK'_1,'wght'_500]">
          Add user
        </h2>
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-mute">
          Created users start ACTIVE · share credentials out-of-band
        </span>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Username *" htmlFor="add-username">
          <input
            id="add-username"
            type="text"
            required
            minLength={3}
            maxLength={32}
            value={form.username}
            onChange={(e) => update("username", e.target.value)}
            className="field-input"
            placeholder="e.g. jane-doe"
          />
        </Field>
        <Field label="Email *" htmlFor="add-email">
          <input
            id="add-email"
            type="email"
            required
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            className="field-input"
            placeholder="jane@example.com"
          />
        </Field>
        <Field label="Password * (min 10 chars)" htmlFor="add-password">
          <input
            id="add-password"
            type="text"
            required
            minLength={10}
            maxLength={128}
            value={form.password}
            onChange={(e) => update("password", e.target.value)}
            className="field-input"
            placeholder="Share this with the user out-of-band"
          />
        </Field>
        <Field label="Designation" htmlFor="add-designation">
          <input
            id="add-designation"
            type="text"
            value={form.designation ?? ""}
            onChange={(e) => update("designation", e.target.value)}
            className="field-input"
            placeholder="e.g. Founder"
          />
        </Field>
        <Field label="First name" htmlFor="add-first-name">
          <input
            id="add-first-name"
            type="text"
            value={form.first_name ?? ""}
            onChange={(e) => update("first_name", e.target.value)}
            className="field-input"
          />
        </Field>
        <Field label="Last name" htmlFor="add-last-name">
          <input
            id="add-last-name"
            type="text"
            value={form.last_name ?? ""}
            onChange={(e) => update("last_name", e.target.value)}
            className="field-input"
          />
        </Field>
      </div>

      {error && (
        <div
          role="alert"
          className="mt-4 rounded border border-plum/40 bg-plum/5 px-4 py-3 font-mono text-xs text-plum"
        >
          {error}
        </div>
      )}

      <div className="mt-5 flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-ghost"
          disabled={submitting}
        >
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? "Creating…" : "Create user"}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.16em] text-ink-mute">
        {label}
      </span>
      {children}
    </label>
  );
}
